import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Cpu, Wifi, Shield, Zap, EyeOff, DollarSign, Users, Home, Server,
  ChevronDown, ArrowRight, Menu, X, Mail, Check, Star, Clock,
  ShoppingCart, BookOpen, Calendar, Wrench, Heart, ListChecks,
  UtensilsCrossed, Box, Lock, Gauge, Network, Globe, Phone,
  BatteryCharging, HardDrive, Bluetooth, Volume2, Activity,
  UserCog, User, Baby, Bot
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function CortexPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [email, setEmail] = useState('');
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [waitlistError, setWaitlistError] = useState('');

  const handleNotify = async (e) => {
    e.preventDefault();
    setWaitlistError('');
    if (!email.includes('@')) {
      setWaitlistError('Please enter a valid email.');
      return;
    }
    try {
      // Marketing subscribe (source: cortex) — also joins v1_cortex_waitlist
      const resp = await fetch(`${BACKEND_URL}/api/public/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source: 'cortex' }),
      });
      if (!resp.ok) throw new Error('subscribe failed');
      setEmailSubmitted(true);
    } catch (err) {
      // Optimistic confirm — capture is best-effort, never block the visitor.
      setEmailSubmitted(true);
    }
  };

  const modes = [
    {
      name: 'Master',
      icon: Home,
      color: '#3EF1C7',
      tagline: 'Your agents, at your service',
      desc: 'Full agent swarm running for your household or business. Meal planning, budgeting, shopping, inventory management, home automation.',
      features: ['All agents active', 'Local AI inference on NPU', 'Real-time family notifications', 'Cloud sync for memory'],
    },
    {
      name: 'Hybrid',
      icon: Activity,
      color: '#7A5CFF',
      tagline: 'Work for you, earn on the side',
      desc: 'Agents run at reduced priority. Excess CPU, NPU, and memory capacity sold to the Commerce Index compute grid. Passive income.',
      features: ['Agents at reduced priority', 'Excess capacity on CI grid', 'Earns CI Credits automatically', 'Seamless mode switching'],
    },
    {
      name: 'Grid',
      icon: Network,
      color: '#FFD166',
      tagline: 'Maximum earnings',
      desc: 'Minimal local services — security monitoring and critical alerts only. Full compute capacity sold to the network. Like solar panels at peak production.',
      features: ['Full capacity on network', 'Peak credit earnings', 'Security + alerts maintained', 'Auto-returns to Master on demand'],
    },
  ];

  const familyMembers = [
    { role: 'Owner', icon: UserCog, tier: 'AUTONOMOUS', tierColor: '#7A5CFF', perms: ['Full agent control', 'Approves all high-value', 'Sets family goals', 'Grid earnings dashboard'] },
    { role: 'Spouse', icon: User, tier: 'AUTONOMOUS', tierColor: '#7A5CFF', perms: ['Co-admin access', 'Shared goals & budget', 'Approves purchases', 'Morning/evening briefs'] },
    { role: 'Teen', icon: Users, tier: 'SUPERVISED', tierColor: '#FFD166', perms: ['Shopping (capped $50)', 'Homework helper', 'Chore tracking', 'Cannot override budget'] },
    { role: 'Child', icon: Baby, tier: 'PROBATION', tierColor: '#F55454', perms: ['Chore reminders', 'Homework tracking', 'Everything needs approval', 'Cannot make purchases'] },
  ];

  const agents = [
    { name: 'Meal Planner', icon: UtensilsCrossed, desc: 'Weekly meals respecting dietary needs, budget, and variety' },
    { name: 'Shopping Agent', icon: ShoppingCart, desc: 'Compares prices, finds deals, places orders with approval' },
    { name: 'Budget Monitor', icon: DollarSign, desc: 'Tracks expenses by category, alerts on overspend' },
    { name: 'Chore Coordinator', icon: ListChecks, desc: 'Assigns tasks fairly, tracks completion, gamifies for kids' },
    { name: 'Calendar Manager', icon: Calendar, desc: 'Detects conflicts, suggests scheduling, coordinates carpools' },
    { name: 'Home Maintenance', icon: Wrench, desc: 'Seasonal reminders, contractor scheduling, warranty tracking' },
    { name: 'Health & Wellness', icon: Heart, desc: 'Medication reminders, appointment scheduling, exercise tracking' },
    { name: 'Homework Helper', icon: BookOpen, desc: 'Schedules study time, tracks assignments, trust-tiered access' },
  ];

  const specs = [
    { label: 'Rockchip RK3588S', detail: '8-core (4× A76 + 4× A55), 2.4GHz' },
    { label: '6 TOPS Neural Engine', detail: 'On-device AI inference (INT4/INT8/INT16/FP16)' },
    { label: '4GB LPDDR5', detail: 'Home edition (8GB Pro, 16GB Max)' },
    { label: '64GB eMMC 5.1', detail: 'Onboard fast storage for system + data' },
    { label: 'WiFi 6 + Bluetooth 5.2', detail: 'Dual-band wireless connectivity' },
    { label: 'Gigabit Ethernet', detail: 'Wired connection for maximum reliability' },
    { label: 'USB-C Power (27W)', detail: '$8/yr electricity — less than a night light' },
    { label: 'Fanless Aluminum', detail: '120 x 90 x 35mm, 0 dB, 280g' },
    { label: 'Auto-Provision', detail: 'Plug in, connect WiFi, done — agents in 60 seconds' },
    { label: 'OTA Updates', detail: 'Firmware updates delivered automatically via CI cloud' },
  ];

  // The brain's layers (exploded hardware view) and the home it connects
  const hardwareLayers = [
    { name: 'Sensing & Mesh Radio', detail: 'WiFi 6 · BT 5.2 · Thread — talks to every device in the house', color: '#FFD166' },
    { name: 'Memory & Storage', detail: 'Your household\'s context — preferences, budgets, routines. Never leaves home.', color: '#7A5CFF' },
    { name: 'Neural Compute (NPU)', detail: '6 TOPS on-device inference — decisions happen locally, in milliseconds', color: '#3EF1C7' },
    { name: 'Fanless Core', detail: '8-core ARM64, silent aluminum, sips 27W — less than a night light', color: '#94A3B8' },
  ];

  const connectedDevices = [
    { name: 'Laptops', icon: Server },
    { name: 'Phones', icon: Phone },
    { name: 'Refrigerator', icon: Box },
    { name: 'Lights', icon: Zap },
    { name: 'Safety & Alarms', icon: Shield },
    { name: 'Water Sensors', icon: Activity },
    { name: 'Microwave', icon: UtensilsCrossed },
    { name: 'Television', icon: Volume2 },
    { name: 'Climate', icon: Gauge },
  ];

  const faqItems = [
    { q: 'What is Cortex?', a: 'Cortex is a physical ARM64 appliance — think of it as a smart router for AI. It sits in your home, runs a team of personal AI agents for your household, and sells excess compute capacity to the Commerce Index network when your agents are idle. It earns you money while you sleep.' },
    { q: 'How does it earn money?', a: 'When your agents are not using the full compute capacity, Cortex offers idle CPU and NPU cycles to the Commerce Index grid. Merchants submit compute jobs (product enrichment, image processing, demand forecasting) and your node executes them in sandboxed containers. You earn CI Credits for each completed job — credits convert to real money through the settlement engine.' },
    { q: 'Do I need Commerce Index to use Cortex?', a: 'Cortex creates a free Commerce Index account automatically on first boot. The household agent features (meal planning, budgeting, chores) work immediately. Grid earnings require the CI network connection. If you are already a CI merchant, Cortex becomes your local AI command center with faster inference and data sovereignty.' },
    { q: 'Is my data safe?', a: 'Your data never leaves your Cortex. Grid compute jobs run in isolated sandboxed containers with encrypted inputs and outputs — the node executes functions, not reads data. Your node identity is protected by ed25519 cryptographic keys. The private key never leaves the device.' },
    { q: 'When does it ship?', a: 'Cortex is planned for 2027. We are not taking money yet — reservations open closer to launch. Join the list with your email and you will be first to know about design reveals, beta units, and ordering.' },
  ];

  const headingStyle = { fontFamily: "'Space Grotesk', sans-serif" };

  return (
    <div className="min-h-screen bg-[#0B1120] text-white">
      {/* ═══════════════════ NAV BAR ═══════════════════ */}
      <nav className="sticky top-0 z-50 border-b border-[#22304A] bg-[#0B1120]/95 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-8">
              <Link to="/" className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-[#3EF1C7] flex items-center justify-center">
                  <span className="text-[#0B1120] font-bold text-sm">CI</span>
                </div>
                <span className="font-semibold text-white">Commerce Index</span>
              </Link>
              <div className="hidden md:flex items-center gap-6">
                <Link to="/" className="text-sm text-[#94A3B8] hover:text-white transition-colors">Home</Link>
                <a href="/#agents" className="text-sm text-[#94A3B8] hover:text-white transition-colors">Agents</a>
                <Link to="/cortex" className="text-sm text-[#FFD166] font-medium">Cortex</Link>
                <a href="/#pricing" className="text-sm text-[#94A3B8] hover:text-white transition-colors">Pricing</a>
                <Link to="/docs" className="text-sm text-[#94A3B8] hover:text-white transition-colors">Docs</Link>
              </div>
            </div>
            <div className="hidden md:flex items-center gap-4">
              <Link to="/login" className="text-sm text-[#94A3B8] hover:text-white transition-colors">Sign In</Link>
              <a href="#waitlist" className="px-4 py-2 bg-[#FFD166] text-[#0B1120] rounded-lg text-sm font-semibold hover:bg-[#FFD166]/90 transition-colors">
                Join the list
              </a>
            </div>
            <button className="md:hidden text-[#94A3B8]" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-[#22304A] bg-[#0B1120] px-4 py-4 space-y-3">
            <Link to="/" className="block text-sm text-[#94A3B8]" onClick={() => setMobileMenuOpen(false)}>Home</Link>
            <Link to="/cortex" className="block text-sm text-[#FFD166] font-medium" onClick={() => setMobileMenuOpen(false)}>Cortex</Link>
            <Link to="/docs" className="block text-sm text-[#94A3B8]" onClick={() => setMobileMenuOpen(false)}>Docs</Link>
            <Link to="/login" className="block text-sm text-[#94A3B8]" onClick={() => setMobileMenuOpen(false)}>Sign In</Link>
            <a href="#waitlist" className="block w-full text-center px-4 py-2 bg-[#FFD166] text-[#0B1120] rounded-lg text-sm font-semibold" onClick={() => setMobileMenuOpen(false)}>Join the list</a>
          </div>
        )}
      </nav>

      {/* ═══════════════════ HERO ═══════════════════ */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-[-40%] left-[-10%] w-[700px] h-[700px] rounded-full bg-[#FFD166]/5 blur-[150px]" />
          <div className="absolute top-[-20%] right-[-15%] w-[500px] h-[500px] rounded-full bg-[#7A5CFF]/5 blur-[120px]" />
          <div className="absolute bottom-[-30%] left-[40%] w-[400px] h-[400px] rounded-full bg-[#3EF1C7]/3 blur-[100px]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-32">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FFD166]/10 border border-[#FFD166]/20 text-[#FFD166] text-xs font-medium mb-8">
                <Clock className="w-3 h-3" /> Coming 2027 — Join the list
              </div>
              <h1 className="text-6xl sm:text-7xl lg:text-8xl font-bold mb-6 tracking-tight" style={headingStyle}>
                Cortex
              </h1>
              <p className="text-2xl sm:text-3xl text-[#E2E8F0] mb-4 font-medium" style={headingStyle}>
                The brain of your home.<br />Commerce included.
              </p>
              <p className="text-lg text-[#94A3B8] mb-10 max-w-lg leading-relaxed">
                One silent device that gives every machine in your house a mind —
                laptops, phones, the refrigerator, lights, alarms, water sensors,
                the microwave, the TV. It watches over your home, buys what you're
                running out of, and earns while it idles.
              </p>
              <div className="flex flex-wrap gap-4">
                <a href="#waitlist" className="inline-flex items-center gap-2 px-8 py-4 bg-[#FFD166] text-[#0B1120] rounded-xl font-semibold hover:bg-[#FFD166]/90 transition-colors text-lg">
                  <Mail className="w-5 h-5" /> Be first to know
                </a>
                <a href="#how-it-works" className="inline-flex items-center gap-2 px-8 py-4 border border-[#334155] text-[#94A3B8] rounded-xl hover:text-white hover:border-[#FFD166]/30 transition-colors text-lg">
                  How it works <ArrowRight className="w-5 h-5" />
                </a>
              </div>
              <p className="text-xs text-[#64748B] mt-4">
                No deposits, no charges — just an email when there's something worth seeing.
              </p>
            </div>

            {/* The brain — exploded hardware layers + connected home ring */}
            <div className="flex flex-col items-center gap-10">
              {/* Exploded layer stack */}
              <div className="relative w-full max-w-sm" style={{ perspective: '900px' }}>
                <div className="absolute inset-0 bg-[#FFD166]/8 blur-[80px] rounded-full scale-110" />
                <div className="relative space-y-[-14px]">
                  {hardwareLayers.map((layer, i) => (
                    <div
                      key={layer.name}
                      className="relative rounded-2xl border p-4 shadow-2xl"
                      style={{
                        transform: `rotateX(55deg) rotateZ(-42deg) translateZ(${(hardwareLayers.length - i) * 6}px)`,
                        transformStyle: 'preserve-3d',
                        background: 'linear-gradient(135deg, #131E36 0%, #0D1628 100%)',
                        borderColor: `${layer.color}44`,
                        boxShadow: `0 ${18 + i * 6}px ${30 + i * 8}px -18px ${layer.color}33`,
                        marginLeft: `${i * 14}px`,
                        zIndex: hardwareLayers.length - i,
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: layer.color }} />
                        <span className="text-xs font-mono tracking-wider" style={{ color: layer.color }}>
                          {layer.name}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
                {/* Layer legend */}
                <div className="mt-8 space-y-2">
                  {hardwareLayers.map(layer => (
                    <div key={layer.name} className="flex items-start gap-2 text-xs">
                      <div className="w-2 h-2 rounded-full mt-1 flex-shrink-0" style={{ background: layer.color }} />
                      <div>
                        <span className="text-[#E2E8F0] font-medium">{layer.name}</span>
                        <span className="text-[#64748B]"> — {layer.detail}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* One brain, every device */}
              <div className="w-full max-w-sm">
                <div className="text-center text-xs uppercase tracking-widest text-[#64748B] mb-4">
                  One brain · every device
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {connectedDevices.map(({ name, icon: Icon }) => (
                    <div key={name} className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-[#0D1628] border border-[#22304A] hover:border-[#FFD166]/30 transition-colors">
                      <Icon className="w-5 h-5 text-[#94A3B8]" />
                      <span className="text-[10px] text-[#64748B] text-center">{name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════ WHAT IS CORTEX ═══════════════════ */}
      <section id="how-it-works" className="py-24 bg-[#0D1628]/50 border-y border-[#22304A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6" style={headingStyle}>
                Intelligence that lives<br />in your home.
              </h2>
              <p className="text-[#94A3B8] leading-relaxed mb-6">
                Cortex is your own sovereign AI node. It runs a team of specialized agents that handle
                the repetitive work of daily life — from meal planning to budgeting to home maintenance.
                Every decision is logged, every action auditable, every agent earns your trust over time
                through the same trust ladder used by Fortune 500 companies on Commerce Index.
              </p>
              <p className="text-[#64748B] leading-relaxed">
                When your agents don't need the full hardware, Cortex sells its idle compute to the
                Commerce Index merchant network — enriching products, processing images, forecasting demand.
                You earn CI Credits that convert to real money. The device pays for itself in weeks.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: Cpu, title: '6 TOPS Neural Engine', desc: 'On-device AI inference. No cloud dependency for your personal agents.', color: '#3EF1C7' },
                { icon: EyeOff, title: 'Silent & Invisible', desc: 'Fanless aluminum body. Zero noise. Smaller than your router.', color: '#7A5CFF' },
                { icon: DollarSign, title: 'Earns While You Sleep', desc: 'Sells idle compute to the CI network. ~$235/yr estimated.', color: '#FFD166' },
                { icon: Shield, title: 'Your Data, Your Node', desc: 'ed25519 cryptographic identity. Your data never leaves the device.', color: '#3EF1C7' },
              ].map(({ icon: Icon, title, desc, color }) => (
                <div key={title} className="bg-[#0D1628] border border-[#22304A] rounded-xl p-5 hover:border-[#334155] transition-colors">
                  <Icon className="w-8 h-8 mb-3" style={{ color }} />
                  <h3 className="font-semibold text-sm mb-2">{title}</h3>
                  <p className="text-xs text-[#64748B] leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════ THREE MODES ═══════════════════ */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={headingStyle}>
              Three modes. One device.
            </h2>
            <p className="text-[#94A3B8] max-w-2xl mx-auto">
              Cortex automatically shifts between modes based on your activity.
              Full power when you need it, full earnings when you don't.
            </p>
          </div>
          <div className="grid lg:grid-cols-3 gap-6">
            {modes.map(({ name, icon: Icon, color, tagline, desc, features }) => (
              <div key={name} className="bg-[#0D1628] rounded-2xl border border-[#22304A] overflow-hidden hover:border-opacity-60 transition-colors group"
                   style={{ borderTopColor: color, borderTopWidth: '2px' }}>
                <div className="p-8">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{ backgroundColor: `${color}15` }}>
                    <Icon className="w-6 h-6" style={{ color }} />
                  </div>
                  <h3 className="text-xl font-bold mb-1" style={headingStyle}>{name} Mode</h3>
                  <p className="text-sm mb-4" style={{ color }}>{tagline}</p>
                  <p className="text-sm text-[#94A3B8] mb-6 leading-relaxed">{desc}</p>
                  <ul className="space-y-2">
                    {features.map(f => (
                      <li key={f} className="flex items-center gap-2 text-xs text-[#64748B]">
                        <Check className="w-3 h-3 flex-shrink-0" style={{ color }} />
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════ THE GRID: HOW YOU EARN ═══════════════════ */}
      <section className="py-24 bg-[#0D1628]/50 border-y border-[#22304A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={headingStyle}>
              The grid. Your income.
            </h2>
            <p className="text-[#94A3B8] max-w-2xl mx-auto">
              Like solar panels sell electricity to the grid, Cortex sells compute to the Commerce Index network.
              Merchants need AI — your idle hardware delivers it.
            </p>
          </div>

          {/* Big numbers */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
            {[
              { value: '~$235', unit: '/yr', label: 'Estimated earnings', color: '#FFD166' },
              { value: '3', unit: ' weeks', label: 'Hardware break-even', color: '#3EF1C7' },
              { value: '$8', unit: '/yr', label: 'Electricity cost', color: '#7A5CFF' },
              { value: '0', unit: ' dB', label: 'Noise level', color: '#3EF1C7' },
            ].map(({ value, unit, label, color }) => (
              <div key={label} className="text-center">
                <div className="text-4xl sm:text-5xl font-bold mb-2" style={{ color, ...headingStyle }}>
                  {value}<span className="text-2xl sm:text-3xl text-[#64748B]">{unit}</span>
                </div>
                <p className="text-sm text-[#64748B]">{label}</p>
              </div>
            ))}
          </div>

          {/* Flow diagram */}
          <div className="bg-[#0D1628] border border-[#22304A] rounded-2xl p-8 lg:p-12">
            <h3 className="text-lg font-semibold mb-8 text-center" style={headingStyle}>How the compute grid works</h3>
            <div className="flex flex-col lg:flex-row items-center justify-between gap-4 lg:gap-2">
              {[
                { icon: Box, label: 'Your Cortex', sub: 'Idle capacity detected', color: '#3EF1C7' },
                { icon: Network, label: 'CI Grid', sub: 'Capacity announced', color: '#7A5CFF' },
                { icon: Server, label: 'Merchant Job', sub: 'Product enrichment', color: '#FFD166' },
                { icon: Shield, label: 'Sandboxed Run', sub: 'Encrypted I/O', color: '#3EF1C7' },
                { icon: DollarSign, label: 'Escrow Release', sub: 'CI Credits earned', color: '#FFD166' },
              ].map(({ icon: Icon, label, sub, color }, i) => (
                <React.Fragment key={label}>
                  <div className="flex flex-col items-center text-center min-w-[120px]">
                    <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-3" style={{ backgroundColor: `${color}15`, border: `1px solid ${color}30` }}>
                      <Icon className="w-6 h-6" style={{ color }} />
                    </div>
                    <span className="text-sm font-medium">{label}</span>
                    <span className="text-[10px] text-[#64748B] mt-1">{sub}</span>
                  </div>
                  {i < 4 && <ArrowRight className="w-5 h-5 text-[#334155] hidden lg:block flex-shrink-0" />}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════ FOR YOUR WHOLE FAMILY ═══════════════════ */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={headingStyle}>
              For your whole family.
            </h2>
            <p className="text-[#94A3B8] max-w-2xl mx-auto">
              Every family member gets their own trust level and agent access.
              Parents control everything. Kids earn autonomy over time — just like agents on Commerce Index.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {familyMembers.map(({ role, icon: Icon, tier, tierColor, perms }) => (
              <div key={role} className="bg-[#0D1628] border border-[#22304A] rounded-xl p-6 hover:border-[#334155] transition-colors">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-[#0B1120] border border-[#22304A] flex items-center justify-center">
                    <Icon className="w-5 h-5 text-[#94A3B8]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-sm">{role}</h3>
                    <span className="text-[10px] font-mono px-2 py-0.5 rounded-full" style={{ color: tierColor, backgroundColor: `${tierColor}15` }}>
                      {tier}
                    </span>
                  </div>
                </div>
                <ul className="space-y-2">
                  {perms.map(p => (
                    <li key={p} className="flex items-start gap-2 text-xs text-[#64748B]">
                      <Check className="w-3 h-3 mt-0.5 flex-shrink-0" style={{ color: tierColor }} />
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════ WHAT RUNS ON IT ═══════════════════ */}
      <section className="py-24 bg-[#0D1628]/50 border-y border-[#22304A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={headingStyle}>
              Your personal agent team.
            </h2>
            <p className="text-[#94A3B8] max-w-2xl mx-auto">
              Pre-loaded with 8 household agents. Each learns your preferences, earns your trust,
              and coordinates with the others — the same architecture that runs Fortune 500 commerce operations.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {agents.map(({ name, icon: Icon, desc }) => (
              <div key={name} className="bg-[#0D1628] border border-[#22304A] rounded-xl p-5 hover:border-[#3EF1C7]/20 transition-colors group">
                <Icon className="w-6 h-6 text-[#3EF1C7] mb-3 group-hover:scale-110 transition-transform" />
                <h3 className="font-semibold text-sm mb-2">{name}</h3>
                <p className="text-xs text-[#64748B] leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════ FOR BUSINESSES TOO ═══════════════════ */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={headingStyle}>
              Already a merchant?
            </h2>
            <p className="text-[#94A3B8] max-w-2xl mx-auto">
              If you're on Commerce Index, Cortex becomes your local AI command center.
              Faster inference, grid earnings to offset your subscription, and full data sovereignty.
            </p>
          </div>
          <div className="grid lg:grid-cols-3 gap-6">
            {[
              { icon: Zap, title: 'Local Inference', desc: 'Agent decisions run on your NPU — faster than cloud round-trips. Sub-100ms for pricing, inventory, and fulfillment decisions.', color: '#3EF1C7' },
              { icon: DollarSign, title: 'Grid Earnings', desc: 'Earn CI Credits from the compute grid. Offset your Commerce Index subscription — or turn a profit.', color: '#FFD166' },
              { icon: Lock, title: 'Data Sovereignty', desc: 'Sensitive operations stay on-device. Pricing strategies, supplier costs, and customer data never leave your node.', color: '#7A5CFF' },
            ].map(({ icon: Icon, title, desc, color }) => (
              <div key={title} className="bg-[#0D1628] border border-[#22304A] rounded-xl p-8 hover:border-[#334155] transition-colors">
                <Icon className="w-8 h-8 mb-4" style={{ color }} />
                <h3 className="text-lg font-semibold mb-3" style={headingStyle}>{title}</h3>
                <p className="text-sm text-[#94A3B8] leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════ INSIDE THE BOX ═══════════════════ */}
      <section className="py-24 bg-[#0D1628]/50 border-y border-[#22304A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-8" style={headingStyle}>
                Inside the box.
              </h2>
              <div className="space-y-4">
                {specs.map(({ label, detail }) => (
                  <div key={label} className="flex items-start gap-3">
                    <Check className="w-4 h-4 text-[#3EF1C7] mt-1 flex-shrink-0" />
                    <div>
                      <span className="font-medium text-sm">{label}</span>
                      <span className="text-sm text-[#64748B] ml-2">— {detail}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-center">
              <div className="bg-[#0D1628] border border-[#22304A] rounded-2xl p-8 w-72">
                <div className="text-center mb-6">
                  <div className="text-3xl font-bold text-[#FFD166] mb-1" style={headingStyle}>Cortex</div>
                  <div className="text-xs text-[#64748B]">120 x 90 x 35mm</div>
                </div>
                <div className="space-y-3 text-xs">
                  {[
                    { icon: Cpu, spec: 'RK3588S 8-core', color: '#3EF1C7' },
                    { icon: Activity, spec: '6 TOPS NPU', color: '#7A5CFF' },
                    { icon: HardDrive, spec: '64GB eMMC', color: '#FFD166' },
                    { icon: Wifi, spec: 'WiFi 6 + BT 5.2', color: '#3EF1C7' },
                    { icon: Globe, spec: 'Gigabit Ethernet', color: '#7A5CFF' },
                    { icon: BatteryCharging, spec: 'USB-C 27W', color: '#FFD166' },
                    { icon: Volume2, spec: '0 dB fanless', color: '#3EF1C7' },
                  ].map(({ icon: Icon, spec, color }) => (
                    <div key={spec} className="flex items-center gap-3 bg-[#0B1120] rounded-lg p-3 border border-[#22304A]">
                      <Icon className="w-4 h-4" style={{ color }} />
                      <span className="text-[#94A3B8]">{spec}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════ PRE-ORDER ═══════════════════ */}
      <section id="waitlist" className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-[-30%] right-[20%] w-[600px] h-[600px] rounded-full bg-[#FFD166]/5 blur-[150px]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={headingStyle}>
              Be first in the future home.
            </h2>
            <p className="text-[#94A3B8] max-w-xl mx-auto">
              We're not taking money yet — reservations open closer to launch.
              Join the list for design reveals, beta units, and first ordering.
            </p>
          </div>

          {/* Email capture */}
          <div className="max-w-lg mx-auto text-center">
            <h3 className="text-lg font-semibold mb-4" style={headingStyle}>Join the Cortex list</h3>
            {emailSubmitted ? (
              <div className="flex items-center justify-center gap-2 text-[#3EF1C7]">
                <Check className="w-5 h-5" />
                <span>You're on the list. Design reveals and launch news, straight to you.</span>
              </div>
            ) : (
              <form onSubmit={handleNotify} className="flex gap-3">
                <div className="flex-1 relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full pl-10 pr-4 py-3 bg-[#0D1628] border border-[#22304A] rounded-xl text-sm text-white placeholder:text-[#64748B] focus:outline-none focus:border-[#FFD166]/50"
                  />
                </div>
                <button type="submit" className="px-6 py-3 bg-[#FFD166] text-[#0B1120] rounded-xl font-semibold hover:bg-[#FFD166]/90 transition-colors whitespace-nowrap">
                  Notify me
                </button>
              </form>
            )}
            {waitlistError && (
              <p className="text-sm text-[#F87171] mt-3">{waitlistError}</p>
            )}
          </div>
        </div>
      </section>

      {/* ═══════════════════ FAQ ═══════════════════ */}
      <section className="py-24 bg-[#0D1628]/50 border-y border-[#22304A]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={headingStyle}>
              Frequently Asked Questions
            </h2>
          </div>
          <div className="space-y-3">
            {faqItems.map((item, i) => (
              <div key={i} className="rounded-xl border border-[#22304A] bg-[#0D1628] overflow-hidden">
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between p-5 text-left">
                  <span className="font-medium text-sm">{item.q}</span>
                  <ChevronDown className={`w-5 h-5 text-[#64748B] transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-5 text-sm text-[#94A3B8] border-t border-[#22304A] pt-4">
                    {item.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════ FINAL CTA ═══════════════════ */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute bottom-[-50%] left-[30%] w-[500px] h-[500px] rounded-full bg-[#FFD166]/5 blur-[120px]" />
        </div>
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6" style={headingStyle}>
            Own your intelligence.
          </h2>
          <p className="text-[#94A3B8] mb-8 text-lg">
            The brain of your home — every device connected, every decision local,
            commerce built in. Coming 2027.
          </p>
          <a href="#waitlist" className="inline-flex items-center gap-2 px-8 py-4 bg-[#FFD166] text-[#0B1120] rounded-xl font-semibold hover:bg-[#FFD166]/90 transition-colors text-lg">
            <Mail className="w-5 h-5" /> Join the Cortex list
          </a>
        </div>
      </section>

      {/* ═══════════════════ FOOTER ═══════════════════ */}
      <footer className="border-t border-[#22304A] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-[#3EF1C7] flex items-center justify-center">
                  <span className="text-[#0B1120] font-bold text-sm">CI</span>
                </div>
                <span className="font-semibold">Commerce Index</span>
              </div>
              <p className="text-xs text-[#64748B]">Agentic Commerce OS. Intelligence for every home and business.</p>
            </div>
            {[
              { title: 'Products', links: [['AI Agents', '/docs/agents'], ['Cortex Hardware', '/cortex'], ['OpenClaw', '/docs/openclaw'], ['CI Proof', '/proof']] },
              { title: 'Cortex', links: [['How it works', '/cortex#how-it-works'], ['Join the list', '/cortex#waitlist'], ['Specs', '/cortex#specs'], ['FAQ', '/cortex#faq']] },
              { title: 'Docs', links: [['Getting Started', '/docs/getting-started'], ['API Reference', '/docs/api-reference'], ['Shopify Guide', '/docs/shopify-setup']] },
              { title: 'Company', links: [['About', '/about'], ['Contact', '/contact'], ['Privacy', '/privacy'], ['Terms', '/terms']] },
            ].map(col => (
              <div key={col.title}>
                <h4 className="font-semibold text-sm mb-4">{col.title}</h4>
                <ul className="space-y-2">
                  {col.links.map(([label, href]) => (
                    <li key={label}>
                      {href.startsWith('http') ? (
                        <a href={href} target="_blank" rel="noopener noreferrer" className="text-xs text-[#64748B] hover:text-[#3EF1C7] transition-colors">{label}</a>
                      ) : (
                        <Link to={href} className="text-xs text-[#64748B] hover:text-[#3EF1C7] transition-colors">{label}</Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="mt-12 pt-8 border-t border-[#22304A] flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-xs text-[#64748B]">&copy; {new Date().getFullYear()} Commerce Index. All rights reserved.</p>
            <div className="flex gap-6">
              <Link to="/privacy" className="text-xs text-[#64748B] hover:text-white">Privacy</Link>
              <Link to="/terms" className="text-xs text-[#64748B] hover:text-white">Terms</Link>
              <Link to="/contact" className="text-xs text-[#64748B] hover:text-white">Contact</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
